import { useState } from 'react';

import {
  AlertTriangle,
  BarChart3,
  ClipboardCheck,
  Clock,
  Download,
  FileSpreadsheet,
  FileText,
  PieChart,
  RefreshCw,
  RotateCcw,
  Scale,
  Send,
} from 'lucide-react';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  AVISO_DE_EXPORTACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { useAuth } from '@/nucleo/auth/useAuth';
import { formatarDinheiro } from '@/nucleo/dinheiro';
import { formatarRelativo } from '@/nucleo/formato/datas';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';
import { StatCardSkeleton } from '@/ui/base/table-skeleton';
import { BarChartCard } from '@/ui/charts/BarChartCard';
import { DashboardCard } from '@/ui/charts/DashboardCard';
import { PieChartCard } from '@/ui/charts/PieChartCard';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { GlossaryTerm } from '@/ui/GlossaryTerm';
import { MonthFilter } from '@/ui/MonthFilter';
import { PageBanner } from '@/ui/PageBanner';
import { PageHeader } from '@/ui/PageHeader';
import { StatsGrid, type StatItem } from '@/ui/StatsGrid';

import { CobrancasDoMes } from './_secoes/CobrancasDoMes';
import { DresEmDefasagem } from './_secoes/DresEmDefasagem';
import { LotesProntos } from './_secoes/LotesProntos';
import { ProximasAcoes } from './_secoes/ProximasAcoes';
import { SemRecortePorDre } from './_secoes/SemRecortePorDre';
import { competenciaDeReferencia, usePainelSme } from './dados';
import { rotuloCurtoDaCompetencia } from './rotulos';

/**
 * O Painel SME — a tela de entrada da área da Secretaria Municipal de Educação
 * (`/sme`), migrada do protótipo (tela SME-001).
 *
 * A SME É PERSONA USUÁRIA, não dona do sistema (CLAUDE.md, topo): este painel é
 * a visão dela sobre o ciclo mensal — o que as DREs enviaram, o que espera
 * decisão, o que está parado e o que já virou dinheiro aprovado. Quem é dono do
 * sistema é a SMT.
 *
 * O QUE NÃO GRAVA, AVISA. Cobrar uma DRE e exportar o painel não têm endpoint —
 * `apps/api/src/modulos/` não tem módulo de painel nem de cobrança. Os botões
 * aparecem (decisão do dono de 2026-09-22, para a demonstração à SMT/DTP) e
 * mostram o aviso honesto de indisponibilidade, no padrão de
 * `SlaAprovacaoSmePage`. Nenhum deles muda estado, nem em memória.
 *
 * QUEM É DRE NÃO VÊ COBRANÇA. `AREA_SME` alcança os quatro papéis de SME e de
 * DRE (mapa.ts, `PAPEIS_SME`), e cobrar diretoria é ato da SME sobre a DRE — uma
 * DRE não cobra a si mesma. Os botões não são escondidos por estilo: não são
 * renderizados (CLAUDE.md, "Regras de interface", item 7).
 *
 * E QUEM É DRE TAMBÉM NÃO VÊ O DADO DAS OUTRAS. Travar a ação não bastava: as
 * listas e o gráfico que comparam diretorias mostravam as treze a qualquer papel
 * de `AREA_SME`. Recortar por diretoria é impossível hoje — `UsuarioAutenticado`
 * (`nucleo/auth/tipos.ts`, o contrato do `GET /eu`) não tem campo de DRE, e
 * inventar a correspondência no mock criaria no front uma noção de escopo que a
 * API real não tem. Então o que compara diretorias mostra o estado que explica a
 * lacuna (`SemRecortePorDre`), como a coluna `Saúde` da Parametrização: a
 * estrutura fica, a afirmação que ninguém apurou não.
 */

/**
 * Quem olha o painel pela visão da SME — todas as diretorias de uma vez.
 *
 * É também quem pode cobrar uma DRE: cobrar diretoria é ato da SME sobre a DRE,
 * e uma DRE não cobra a si mesma. Os dois recortes coincidem hoje, e por isso são
 * a mesma lista: separá-los em duas constantes iguais convidaria a divergirem.
 */
const PAPEIS_DA_VISAO_SME = ['ADMIN', 'GESTOR_ADMIN', 'GESTOR_SME', 'OPERADOR_SME'] as const;

export function PainelSmePage() {
  const { temPapel } = useAuth();
  const [competencia, definirCompetencia] = useState(() => competenciaDeReferencia());

  const consulta = usePainelSme(competencia);
  const painel = consulta.data;
  const ehVisaoSme = temPapel(...PAPEIS_DA_VISAO_SME);
  // O que compara diretorias só aparece para a SME: ver `SemRecortePorDre`.
  const semRecortePorDre = !ehVisaoSme;
  const podeCobrar = ehVisaoSme;

  const avisarIndisponivel = () => mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
  const exportar = () => mostrarAviso(AVISO_DE_EXPORTACAO_INDISPONIVEL);

  const indicadores = painel?.indicadores;
  const variacoes = painel?.variacoes ?? {};

  const cartoesDeDiretoria: StatItem[] = indicadores
    ? [
        {
          title: 'DREs em atraso de envio',
          value: indicadores.dresEmAtrasoDeEnvio,
          icon: Clock,
          description:
            indicadores.dresEmAtrasoDeEnvio === 0
              ? 'Todas as DREs enviaram apontamentos dentro do prazo'
              : 'Apontamentos não enviados dentro do prazo da competência',
        },
        {
          title: 'DREs com lote vencido',
          value: indicadores.dresComLoteVencido,
          icon: AlertTriangle,
          description: `${indicadores.lotesAcimaDoSla} lote(s) acima do SLA de aprovação SME`,
        },
      ]
    : [];

  const cartoesDoCiclo: StatItem[] = indicadores
    ? [
        {
          title: 'Lotes aguardando aprovação',
          value: indicadores.lotesAguardandoAprovacao,
          icon: ClipboardCheck,
          description: 'Apontamentos enviados pelas DREs',
          trend:
            variacoes.lotesAguardandoAprovacao === undefined
              ? undefined
              : { value: variacoes.lotesAguardandoAprovacao, label: 'vs. mês anterior' },
        },
        {
          title: 'Lotes devolvidos',
          value: indicadores.lotesDevolvidos,
          icon: RotateCcw,
          description: 'Aguardando correção da DRE',
          trend:
            variacoes.lotesDevolvidos === undefined
              ? undefined
              : { value: variacoes.lotesDevolvidos, label: 'vs. mês anterior' },
        },
        {
          title: 'Revisões abertas',
          value: indicadores.revisoesAbertas,
          icon: Scale,
          description: 'Pedidos de revisão de medições',
          trend:
            variacoes.revisoesAbertas === undefined
              ? undefined
              : { value: variacoes.revisoesAbertas, label: 'vs. mês anterior' },
        },
        {
          title: 'Aguardando documentos',
          value: indicadores.aguardandoDocumentos,
          icon: FileText,
          description: 'Pré-liquidação (NF, INSS, FGTS…)',
          trend:
            variacoes.aguardandoDocumentos === undefined
              ? undefined
              : { value: variacoes.aguardandoDocumentos, label: 'vs. mês anterior' },
        },
        {
          title: 'Medições do mês',
          value: indicadores.medicoesDoMes,
          icon: FileSpreadsheet,
          description: 'Competência atual',
          trend:
            variacoes.medicoesDoMes === undefined
              ? undefined
              : { value: variacoes.medicoesDoMes, label: 'vs. mês anterior' },
        },
        {
          title: 'Demandas em aberto',
          value: indicadores.demandasEmAberto,
          icon: Send,
          description: 'Solicitações SME → DTP aguardando',
        },
      ]
    : [];

  /**
   * Os três cartões que falam de OUTRAS diretorias ficam só na visão da SME.
   * Para quem é DRE eles não são afirmação falsa — são contagem da cidade
   * inteira —, mas também não são resposta nenhuma à pergunta que essa pessoa
   * traz ("e a minha?"), e é essa pergunta que o painel ainda não sabe responder.
   */
  const cartoes: StatItem[] = ehVisaoSme
    ? [
        ...cartoesDeDiretoria,
        ...cartoesDoCiclo,
        {
          title: 'Cobranças DRE no mês',
          value: indicadores?.cobrancasNoMes ?? 0,
          icon: Send,
          description: `${indicadores?.dresAlcancadasPorCobranca ?? 0} DRE(s) alcançada(s) na competência`,
        },
      ]
    : cartoesDoCiclo;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Painel SME"
        description={
          indicadores
            ? `${indicadores.demandasEmAberto} demanda(s) SME→DTP em aberto`
            : 'Visão da Secretaria Municipal de Educação sobre o ciclo mensal.'
        }
      >
        {painel && (
          <span className="text-xs text-muted-foreground">
            Atualizado {formatarRelativo(painel.calculadoEm)}
          </span>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void consulta.refetch()}
          disabled={consulta.isFetching}
        >
          <RefreshCw className="mr-1.5 h-4 w-4" aria-hidden="true" />
          Atualizar
        </Button>
        {/*
          Rótulo visível, e não `hideLabel`: o protótipo escreve "Competência:"
          ao lado do seletor, e o cabeçalho desta tela tem espaço para ele. O
          `hideLabel` do `MonthFilter` passou a manter o rótulo em sr-only na PR
          #124 — antes dela, escondia-o de vez —, então as duas opções são
          acessíveis hoje; a escolha aqui é de fidelidade ao protótipo.
        */}
        <MonthFilter
          value={competencia}
          onChange={definirCompetencia}
          id="competencia-painel-sme"
        />
        <Button variant="outline" size="sm" onClick={exportar}>
          <Download className="mr-1.5 h-4 w-4" aria-hidden="true" />
          Exportar
        </Button>
      </PageHeader>

      {semRecortePorDre && (
        <PageBanner
          tone="info"
          title="Este painel ainda não tem recorte por diretoria"
          description="Os números abaixo são da cidade inteira. O que compara diretorias — cobranças, defasagem, lotes por DRE e a fila de aprovação — depende de uma rota que ainda não existe, e por isso abre apenas para a SME."
        />
      )}

      {consulta.isLoading ? (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" role="status">
          {Array.from({ length: 9 }, (_, indice) => (
            <StatCardSkeleton key={indice} />
          ))}
          <p className="sr-only">Carregando o painel da SME.</p>
        </div>
      ) : consulta.isError ? (
        <ErrorState
          variant="load-failed"
          title="Não foi possível carregar o painel da SME"
          primaryAction={{ label: 'Tentar de novo', onClick: () => void consulta.refetch() }}
        />
      ) : !painel ? null : (
        <>
          <StatsGrid stats={cartoes} columns="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" />

          <ProximasAcoes
            lotesAguardandoAprovacao={painel.indicadores.lotesAguardandoAprovacao}
            demandasEmAberto={painel.indicadores.demandasEmAberto}
            documentosPendentes={painel.documentosPendentesDeValidacao}
            mensagensNaoLidas={painel.mensagensNaoLidas}
          />

          <CobrancasDoMes
            dres={painel.dresEmDefasagem}
            cobrancasDisparadas={painel.indicadores.cobrancasNoMes}
            dresAlcancadas={painel.indicadores.dresAlcancadasPorCobranca}
            podeCobrar={podeCobrar}
            semRecortePorDre={semRecortePorDre}
            aoCobrar={avisarIndisponivel}
            aoCobrarPendentes={avisarIndisponivel}
          />

          <div className="grid gap-4 lg:grid-cols-2">
            {painel.lotesPorStatus.length === 0 ? (
              <DashboardCard title="Distribuição de lotes por status" icon={PieChart}>
                <EmptyState
                  title="Sem lotes na competência"
                  description="Nenhum lote foi aberto pelas DREs nesta competência."
                />
              </DashboardCard>
            ) : (
              <PieChartCard
                title="Distribuição de lotes por status"
                icon={PieChart}
                data={painel.lotesPorStatus.map((fatia) => ({
                  name: fatia.situacao,
                  value: fatia.quantidade,
                }))}
              />
            )}

            {semRecortePorDre ? (
              <DashboardCard title="Lotes por DRE" icon={BarChart3}>
                <SemRecortePorDre />
              </DashboardCard>
            ) : painel.lotesPorDre.length === 0 ? (
              <DashboardCard title="Lotes por DRE" icon={BarChart3}>
                <EmptyState
                  title="Sem lotes por DRE"
                  description="Nenhum apontamento enviado nesta competência."
                />
              </DashboardCard>
            ) : (
              <BarChartCard
                title="Lotes por DRE"
                data={painel.lotesPorDre.map((barra) => ({
                  name: barra.dreCodigo,
                  total: barra.quantidade,
                }))}
              />
            )}
          </div>

          {painel.volumeAprovado.length === 0 ? (
            <DashboardCard title="Volume financeiro aprovado — últimos 6 meses" icon={BarChart3}>
              <EmptyState
                title="Sem volume aprovado"
                description="Nenhuma medição aprovada nos últimos seis meses."
              />
            </DashboardCard>
          ) : (
            <BarChartCard
              title="Volume financeiro aprovado — últimos 6 meses"
              data={painel.volumeAprovado.map((mes) => ({
                name: rotuloCurtoDaCompetencia(mes.competencia),
                total: mes.valorAprovado,
              }))}
              height={280}
              yAxisFormatter={(valor) => `${Math.round(valor / 1000)}k`}
              tooltipFormatter={(valor) => formatarDinheiro(valor)}
            />
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            <DresEmDefasagem
              dres={painel.dresEmDefasagem}
              podeCobrar={podeCobrar}
              semRecortePorDre={semRecortePorDre}
              aoCobrar={avisarIndisponivel}
              aoCobrarTodas={avisarIndisponivel}
            />
            <LotesProntos
              lotes={painel.lotesProntosParaAprovacao}
              semRecortePorDre={semRecortePorDre}
            />
          </div>

          <Card className="border-border/60">
            <CardContent className="flex flex-wrap items-center gap-x-3 gap-y-1 p-3 text-xs text-muted-foreground">
              <span>Termos:</span>
              <GlossaryTerm termo="SLA_APROVACAO_SME">SLA SME</GlossaryTerm>
              <GlossaryTerm termo="LOTE_APONTAMENTO">Lote</GlossaryTerm>
              <GlossaryTerm termo="REVISAO_MEDICAO">Revisão de medição</GlossaryTerm>
              <GlossaryTerm termo="DRE_DEFASADA">DRE em defasagem</GlossaryTerm>
              <GlossaryTerm termo="COBERTURA_DRE">Cobertura por DRE</GlossaryTerm>
              <GlossaryTerm termo="DEMANDA_SME">Demanda SME→DTP</GlossaryTerm>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

export default PainelSmePage;
